import { useRef, useState, type ReactNode } from "react";

import { useProjectOverviewQuery } from "@/api/hooks";
import { usePreviewCandidatesQuery } from "@/api/preview";
import {
  classifySimRunFailure,
  useCreatePreviewSimRunMutation,
  usePreviewSimPersonasQuery,
  type SimRunFailure,
} from "@/api/previewSim";
import { useCapability } from "@/auth/SessionContext";
import {
  SIM_DEFAULT_TURNS,
  SIM_LANGUAGES,
  SIM_LANGUAGE_LABELS,
  SIM_MAX_TURNS,
  SIM_MIN_TURNS,
  personasFor,
  readMaxTurns,
  type SimLanguage,
} from "@/lib/previewSim";
import { Button } from "@/ui/Button";
import { Input, Select } from "@/ui/Input";
import { LabBarField, LabMoreMenu } from "./LabBarField";
import { defaultLabFlags, type LabFlags } from "@/lib/labServices";

import { LabNewManualForm } from "./LabNewManualForm";
import { LabRunOptions } from "./LabRunOptions";
import { LabSkipBackground } from "./LabStartAfterBackground";

/**
 * The refusals, in a clinician's words (same table as SimulatedPatientPanel,
 * which does not export it). `satisfies` so a new kind fails the build.
 */
const FAILURE_MESSAGES = {
  stale_target:
    "This project's preview target changed since you opened this page. Reload, then run again.",
  duplicate_command:
    "That run request was already used for a different run. Run again to start a new one.",
  admission_refused:
    "The preview target refused this testing version's safety posture, so no run was started.",
  candidate_unusable:
    "This testing version is no longer available or has expired. Freeze a new one from the Preview dialog.",
  language_unavailable:
    "This preview target does not offer this language for a conversation, so a run cannot be set up. Choose another language.",
  candidate_validator_mismatch:
    "This testing version was validated by another engine build and must be rebuilt before it can run.",
  preview_target:
    "This project's preview target is not available for previews right now.",
  denied: "Your account cannot start simulated-patient runs here.",
  unknown_dispatch:
    "The service did not confirm whether the run started. Run again to retry: the same request cannot start a second run.",
  error: "The run could not be started.",
} as const satisfies Record<SimRunFailure["kind"], string>;

/**
 * Start a simulated-patient run on the testing version chosen in the top bar.
 * One idempotency key per attempt: a changed form mints a new one, a retry
 * after a lost answer reuses it and cannot start a second run.
 */
function AutoRunForm({
  projectId,
  candidateId,
  onStarted,
}: {
  projectId: string;
  candidateId: string | null;
  onStarted: (previewRunRef: string) => void;
}) {
  const create = useCapability("preview.create");
  const overview = useProjectOverviewQuery(projectId);
  const candidates = usePreviewCandidatesQuery(projectId);
  const personas = usePreviewSimPersonasQuery(projectId, {
    enabled: create.allowed,
  });
  const mutation = useCreatePreviewSimRunMutation(projectId);

  const [language, setLanguage] = useState<SimLanguage>("he");
  const [personaId, setPersonaId] = useState("");
  // Default: run until Liv ends the conversation (the service's maximum).
  const [untilLivEnds, setUntilLivEnds] = useState(true);
  const [turnsText, setTurnsText] = useState(String(SIM_DEFAULT_TURNS));
  const [labFlags, setLabFlags] = useState<LabFlags>(defaultLabFlags);
  const idempotencyKey = useRef<string>(crypto.randomUUID());

  const choices = personasFor(personas.data?.personas ?? [], language);
  const selectedPersona = choices.find((choice) => choice.id === personaId);
  const maxTurns = untilLivEnds ? SIM_MAX_TURNS : readMaxTurns(turnsText);
  const candidate =
    candidates.data?.items.find((item) => item.candidateId === candidateId) ??
    null;
  const revision = overview.data?.project.previewTargetRevision;
  const failure = mutation.isError
    ? classifySimRunFailure(mutation.error)
    : null;

  const blocker = !create.allowed
    ? `${create.reason} Running a simulated patient needs the "preview.create" permission.`
    : candidate === null
      ? candidates.isPending
        ? "Loading the testing version…"
        : "Choose a testing version in the top bar. Freeze one from the Preview dialog if there is none."
      : candidate.validity?.state === "expired"
        ? "This testing version has expired. Choose another one in the top bar."
        : revision === undefined
          ? "Loading the project…"
          : selectedPersona === undefined
            ? "Choose a persona."
            : maxTurns === null
              ? `Enter a turn budget from ${SIM_MIN_TURNS} to ${SIM_MAX_TURNS}.`
              : null;

  /** A changed form is a different run: it gets its own key, and an old refusal stops applying. */
  function changed() {
    idempotencyKey.current = crypto.randomUUID();
    mutation.reset();
  }

  function run() {
    if (
      blocker !== null ||
      candidate === null ||
      revision === undefined ||
      maxTurns === null
    ) {
      return;
    }
    mutation.mutate(
      {
        candidateId: candidate.candidateId,
        personaId,
        language,
        maxTurns,
        labFlags,
        expectedPreviewTargetRevision: revision,
        idempotencyKey: idempotencyKey.current,
      },
      {
        onSuccess(created) {
          // Pressing Run again now means ANOTHER run; only an unconfirmed
          // dispatch keeps its key.
          idempotencyKey.current = crypto.randomUUID();
          onStarted(created.previewRunRef);
        },
      },
    );
  }

  return (
    <form
      className="contents"
      onSubmit={(event) => {
        event.preventDefault();
        run();
      }}
    >
      <LabBarField label="Language">
        {({ inputId }) => (
          <Select
            id={inputId}
            className="w-28"
            value={language}
            onChange={(event) => {
              setLanguage(event.target.value as SimLanguage);
              setPersonaId("");
              changed();
            }}
          >
            {SIM_LANGUAGES.map((code) => (
              <option key={code} value={code}>
                {SIM_LANGUAGE_LABELS[code]}
              </option>
            ))}
          </Select>
        )}
      </LabBarField>
      <LabBarField label="Persona">
        {({ inputId }) => (
          <Select
            id={inputId}
            className="w-64"
            value={personaId}
            aria-disabled={choices.length === 0 || undefined}
            onChange={(event) => {
              setPersonaId(event.target.value);
              changed();
            }}
          >
            <option value="">
              {personas.isPending && create.allowed
                ? "Loading…"
                : choices.length === 0
                  ? "None offered in this language"
                  : "Choose a persona"}
            </option>
            {choices.map((choice) => (
              <option key={choice.id} value={choice.id}>
                {choice.title}
              </option>
            ))}
          </Select>
        )}
      </LabBarField>
      {selectedPersona ? (
        <details className="relative">
          <summary className="inline-flex h-9 cursor-pointer list-none items-center rounded-md border border-border bg-card px-3 text-sm font-medium hover:bg-secondary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring">
            About this persona
          </summary>
          <p
            dir="auto"
            className="absolute start-0 z-30 mt-1 max-h-48 w-80 overflow-y-auto rounded-md border border-border bg-card p-3 text-sm shadow-md"
          >
            {selectedPersona.description}
          </p>
        </details>
      ) : null}
      <label className="flex h-9 items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={untilLivEnds}
          onChange={(event) => {
            setUntilLivEnds(event.target.checked);
            changed();
          }}
        />
        Until Liv ends
      </label>
      {untilLivEnds ? null : (
        <LabBarField label="Max turns">
          {({ inputId }) => (
            <Input
              id={inputId}
              className="w-20"
              inputMode="numeric"
              value={turnsText}
              onChange={(event) => {
                setTurnsText(event.target.value);
                changed();
              }}
              aria-invalid={maxTurns === null ? true : undefined}
            />
          )}
        </LabBarField>
      )}

      <LabRunOptions
        value={labFlags}
        onChange={(next) => {
          setLabFlags(next);
          changed();
        }}
      />

      {personas.isError ? (
        <p role="alert" className="order-last basis-full text-sm text-destructive">
          The persona list could not be loaded from the preview target.
        </p>
      ) : null}

      <Button
        type="submit"
        disabled={blocker !== null || mutation.isPending}
        title={blocker ?? undefined}
      >
        {mutation.isPending
          ? "Starting…"
          : failure?.kind === "unknown_dispatch"
            ? "Retry run"
            : <><span aria-hidden="true">▶</span> Play</>}
      </Button>
      {blocker !== null ? (
        <p className="order-last basis-full text-xs text-muted-foreground">{blocker}</p>
      ) : null}

      {failure !== null ? (
        <p
          role="alert"
          className="order-last basis-full rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive"
        >
          {FAILURE_MESSAGES[failure.kind]}
        </p>
      ) : null}
    </form>
  );
}

/** The Brain cannot skip background, so "Skip BG" replays a run that got past it. */
type BgMode = "all" | "skip";

const BG_MODE_KEY = "lab.bgMode";
const BG_MODES: readonly BgMode[] = ["all", "skip"];

function readBgMode(): BgMode {
  try {
    const stored = window.localStorage.getItem(BG_MODE_KEY);
    return BG_MODES.find((mode) => mode === stored) ?? "all";
  } catch {
    return "all";
  }
}

function writeBgMode(mode: BgMode): void {
  try {
    window.localStorage.setItem(BG_MODE_KEY, mode);
  } catch {
    // Storage blocked: the choice applies to this page only.
  }
}

/** The settings bar: Manual = the clinician plays the patient, Patient Sim = the simulated patient drives it. */
export function LabNewRunForm({
  projectId,
  candidateId,
  onStarted,
  more,
}: {
  projectId: string;
  candidateId: string | null;
  onStarted: (previewRunRef: string) => void;
  more?: ReactNode;
}) {
  const [mode, setMode] = useState<"auto" | "manual">("manual");
  const [bgMode, setBgMode] = useState<BgMode>(readBgMode);
  const [chatSlot, setChatSlot] = useState<HTMLElement | null>(null);
  const manualStart = mode === "manual" && bgMode === "all";
  return (
    <section
      aria-label="Conversation settings"
      className="flex flex-wrap items-end gap-x-4 gap-y-3 border-b border-border bg-card px-4 py-3 [&_input:not([type=checkbox])]:h-9 [&_select]:h-9"
    >
      <LabBarField label="Mode">
        {({ inputId }) => (
          <Select
            id={inputId}
            className="w-36"
            value={mode}
            onChange={(event) =>
              setMode(event.target.value as "auto" | "manual")
            }
          >
            <option value="manual">Manual</option>
            <option value="auto">Patient Sim</option>
          </Select>
        )}
      </LabBarField>
      {mode === "manual" ? (
        <LabBarField label="BG mode">
          {({ inputId }) => (
            <Select
              id={inputId}
              className="w-52"
              value={bgMode}
              onChange={(event) => {
                const next = event.target.value as BgMode;
                setBgMode(next);
                writeBgMode(next);
              }}
            >
              <option value="all">All BG</option>
              <option value="skip">Skip BG</option>
            </Select>
          )}
        </LabBarField>
      ) : null}
      {mode === "auto" ? (
        <AutoRunForm
          projectId={projectId}
          candidateId={candidateId}
          onStarted={onStarted}
        />
      ) : bgMode === "skip" ? (
        <LabSkipBackground
          projectId={projectId}
          candidateId={candidateId}
          onStarted={onStarted}
        />
      ) : (
        <LabNewManualForm
          projectId={projectId}
          candidateId={candidateId}
          onStarted={onStarted}
          chatSlot={chatSlot}
        />
      )}
      {manualStart || more !== undefined ? (
        <LabMoreMenu>
          <div ref={setChatSlot} hidden={!manualStart} />
          {more}
        </LabMoreMenu>
      ) : null}
      {mode === "manual" && bgMode !== "all" ? (
        <p className="order-last basis-full text-xs text-muted-foreground">
          Skip BG replays an earlier conversation, which cannot take the Safety
          and Injection check choices: the target&apos;s defaults apply.
        </p>
      ) : null}
    </section>
  );
}
